/**
 * Purchase Order / Delivery Order queries — PRD.192.
 *
 * Boundary: PO save and DO draft save do NOT write to stock_movements.
 * Only DO finalize writes inbound rows with source_type='delivery_order'
 * and quantity_in > 0. The data-access layer only exposes reads.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row types ───

export interface PurchaseOrderRow {
  id: string;
  org_id: string | null;
  po_number: string | null;
  po_date: string | null;
  // PRD.207 — link to inventory_suppliers (nullable for legacy free-text rows).
  supplier_id: string | null;
  supplier_name: string | null;
  amounts_are: string | null; // 'tax_exclusive' | 'tax_inclusive'
  notes: string | null;
  status: string | null; // 'draft' | 'posted' | 'cancelled'
  status_executor: string | null;
  total_amount: number | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PurchaseOrderLineRow {
  id: string;
  org_id: string | null;
  purchase_order_id: string | null;
  inventory_item_id: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  unit_snapshot: string | null;
  quantity_ordered: number | null;
  bonus_quantity: number | null;
  rate: number | null;
  amount: number | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DeliveryOrderRow {
  id: string;
  org_id: string | null;
  do_number: string | null;
  purchase_order_id: string | null;
  // PRD.207 — link to inventory_suppliers (nullable for legacy free-text rows).
  supplier_id: string | null;
  supplier_name: string | null;
  supplier_invoice_number: string | null;
  reference: string | null;
  date_raised: string | null;
  delivery_date: string | null;
  received_date: string | null;
  amounts_are: string | null;
  subtotal: number | null;
  invoice_total: number | null;
  notes: string | null;
  status: string | null; // 'draft' | 'received' | 'cancelled'
  received_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DeliveryOrderLineRow {
  id: string;
  org_id: string | null;
  delivery_order_id: string | null;
  purchase_order_line_id: string | null;
  inventory_item_id: string | null;
  inventory_batch_id: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  unit_snapshot: string | null;
  purchasing_unit_ordered: string | null;
  quantity_ordered: number | null;
  quantity_received: number | null;
  bonus_quantity_received: number | null;
  unit_price: number | null;
  unit_price_without_bonus: number | null;
  rate: number | null;
  amount: number | null;
  batch_number_snapshot: string | null;
  expiry_date_snapshot: string | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── Queries ───

export function qPurchaseOrdersList(): QuerySpec<[]> {
  return {
    key: ['purchase-orders-list'] as const,
    sql: `
      SELECT * FROM purchase_orders
      ORDER BY po_date DESC, created_at DESC
    `,
    params: [],
    watch: ['purchase_orders'],
  };
}

export function qPurchaseOrderLinesByOrder(purchaseOrderId: string): QuerySpec<[string]> {
  return {
    key: ['purchase-order-lines', purchaseOrderId] as const,
    sql: `
      SELECT * FROM purchase_order_lines
      WHERE purchase_order_id = ?
      ORDER BY created_at ASC
    `,
    params: [purchaseOrderId],
    watch: ['purchase_order_lines'],
  };
}

/**
 * Read a single PO header by id, returning at most one row. Useful when
 * preloading a Receive Delivery Order page from a PO link.
 */
export function qPurchaseOrderForReceive(purchaseOrderId: string): QuerySpec<[string]> {
  return {
    key: ['purchase-order-for-receive', purchaseOrderId] as const,
    sql: `SELECT * FROM purchase_orders WHERE id = ? LIMIT 1`,
    params: [purchaseOrderId],
    watch: ['purchase_orders'],
  };
}

export function qDeliveryOrdersList(): QuerySpec<[]> {
  return {
    key: ['delivery-orders-list'] as const,
    sql: `
      SELECT * FROM delivery_orders
      ORDER BY received_date DESC, delivery_date DESC, created_at DESC
    `,
    params: [],
    watch: ['delivery_orders'],
  };
}

export function qDeliveryOrderLinesByOrder(deliveryOrderId: string): QuerySpec<[string]> {
  return {
    key: ['delivery-order-lines', deliveryOrderId] as const,
    sql: `
      SELECT * FROM delivery_order_lines
      WHERE delivery_order_id = ?
      ORDER BY created_at ASC
    `,
    params: [deliveryOrderId],
    watch: ['delivery_order_lines'],
  };
}

export function qDeliveryOrdersByPurchaseOrder(purchaseOrderId: string): QuerySpec<[string]> {
  return {
    key: ['delivery-orders-by-po', purchaseOrderId] as const,
    sql: `
      SELECT * FROM delivery_orders
      WHERE purchase_order_id = ?
      ORDER BY created_at DESC
    `,
    params: [purchaseOrderId],
    watch: ['delivery_orders'],
  };
}

/**
 * Inbound stock movements created by a specific Delivery Order. Used to
 * verify a finalize wrote ledger rows (e.g. for the DO detail view).
 */
export function qDeliveryOrderStockMovements(deliveryOrderId: string): QuerySpec<[string]> {
  return {
    key: ['stock-movements-by-do', deliveryOrderId] as const,
    sql: `
      SELECT * FROM stock_movements
      WHERE source_type = 'delivery_order' AND source_id = ?
      ORDER BY created_at ASC
    `,
    params: [deliveryOrderId],
    watch: ['stock_movements'],
  };
}
